// The route tree (plan §4.2): legacy paths 1:1 under `/l/$slug`, the login page, and the legacy redirects (a section
// root to its first tab). Titles are the toolbar's page titles (legacy `appStore.loadPageConfig`).
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, useRouterState, type RouterHistory, type RouteComponent } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { choosingAccount, currentSession, endSession, refreshSession, sessionSnapshot, loginSearch, switchTo } from '../session/session.ts';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { NotFoundPage } from '../features/auth/NotFoundPage.tsx';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from '../features/auth/AuthPages.tsx';
import { AccountsPage } from '../features/accounts/AccountsPage.tsx';
import { AccountDetailPage, StaffLocationPage } from '../features/accounts/AccountDetailPage.tsx';
import { staffMenuOf } from '../shell/StaffShell.tsx';
const deviceClaims = () => import('../features/hardware/DeviceClaimsPage.tsx');
const staffUsers = () => import('../features/users/StaffUsersPage.tsx');
// Route-level code splitting (plan §9 budgets): each area's pages load when first opened.
const dashboard = () => import('../features/dashboard/DashboardPage.tsx');
const discounts = () => import('../features/discounts/DiscountsPage.tsx');
const menu = () => import('../features/catalog/MenuPages.tsx');
const items = () => import('../features/catalog/ItemsPage.tsx');
const modifiers = () => import('../features/catalog/ModifiersPage.tsx');
const departments = () => import('../features/catalog/DepartmentsPage.tsx');
const displayOrder = () => import('../features/catalog/DisplayOrderPage.tsx');
const visibility = () => import('../features/catalog/CategoryVisibilityPage.tsx');
const employees = () => import('../features/employees/EmployeesPage.tsx');
const timecards = () => import('../features/employees/TimecardsPage.tsx');
const schedule = () => import('../features/employees/SchedulePage.tsx');
const scheduleTemplates = () => import('../features/employees/ScheduleTemplatesPage.tsx');
const timeOff = () => import('../features/employees/TimeOffPage.tsx');
const shiftClaims = () => import('../features/employees/ShiftClaimsPage.tsx');
const shiftSwaps = () => import('../features/employees/ShiftSwapsPage.tsx');
const availability = () => import('../features/employees/AvailabilityPage.tsx');
const employeeDetail = () => import('../features/employees/EmployeeDetailPage.tsx');
const jobRoles = () => import('../features/employees/JobRolesPage.tsx');
const breaks = () => import('../features/employees/EmployeeBreaksPage.tsx');
const staffTips = () => import('../features/employees/StaffTipsPage.tsx');
const payroll = () => import('../features/employees/PayrollPage.tsx');
const breaksReport = () => import('../features/employees/BreaksPage.tsx');
const reports = () => import('../features/reports/ReportPages.tsx');
const drawersList = () => import('../features/reports/DrawersPage.tsx');
const salesReports = () => import('../features/reports/SalesPages.tsx');
const adjustments = () => import('../features/reports/AdjustmentsPage.tsx');
const payment = () => import('../features/settings/PaymentConfigurationPage.tsx');
const generated = () => import('../features/settings/GeneratedSettingsPage.tsx');
const floor = () => import('../features/floor/FloorPlansPage.tsx');
const onlineForms = () => import('../features/online/StoreSettingsPages.tsx');
const qrSettings = () => import('../features/qr/QrSettingsPage.tsx');
const qrCodes = () => import('../features/qr/QrCodesPage.tsx');
const qrTemplates = () => import('../features/qr/templates/TemplatesPage.tsx');
const staffQrTemplates = () => import('../features/qr/templates/StaffQrTemplatesPage.tsx');
const onlineHours = () => import('../features/online/HoursPage.tsx');
const onlineLists = () => import('../features/online/ListPages.tsx');
const onlineVoice = () => import('../features/online/VoicePages.tsx');
const onlineKeys = () => import('../features/online/ApiKeysPage.tsx');
const hardware = () => import('../features/hardware/PrintersPage.tsx');
const devices = () => import('../features/hardware/PosDevicesPage.tsx');
const branding = () => import('../features/branding/BrandingPage.tsx');
const profile = () => import('../features/profile/ProfilePage.tsx');
const locationInfo = () => import('../features/branding/LocationInfoPage.tsx');
const voids = () => import('../features/voids/VoidReasonsPage.tsx');
const tenderTypes = () => import('../features/settings/TenderTypesPage.tsx');
const diningOptions = () => import('../features/settings/DiningOptionsPage.tsx');
const timeSegments = () => import('../features/settings/TimeSegmentsPage.tsx');
const pacing = () => import('../features/settings/PacingPage.tsx');
const idVerification = () => import('../features/settings/IdVerificationPage.tsx');
const customers = () => import('../features/customers/CustomersPage.tsx');
const customerDetail = () => import('../features/customers/CustomerDetailPage.tsx');
const orderLookup = () => import('../features/orders/OrderLookupPage.tsx');
const users = () => import('../features/users/UsersPage.tsx');
const roles = () => import('../features/users/UserRolesPage.tsx');
const scheduling = () => import('../features/settings/SchedulingSettingsPage.tsx');
const overtime = () => import('../features/settings/OvertimeSettingsPage.tsx');
const giftCards = () => import('../features/gift-cards/GiftCardsReportPage.tsx');
const giftCardsWelcome = () => import('../features/gift-cards/GiftCardsWelcomePage.tsx');
const adminCenter = () => import('../features/gift-cards/AdminCenterGiftCardsPage.tsx');
const adminCenterLayout = () => import('../features/gift-cards/AdminCenterLayout.tsx');
const billingFeatures = () => import('../features/billing/BillingFeaturesPage.tsx');
const billingPlans = () => import('../features/billing/BillingPlansPage.tsx');
const billingAdjustments = () => import('../features/billing/BillingAdjustmentsPage.tsx');
const billingNotYetOnPlan = () => import('../features/billing/BillingNotYetOnPlanPage.tsx');
const plansAndBilling = () => import('../features/billing/PlansAndBillingPage.tsx');
const DiscountsPage = lazyRouteComponent(discounts, 'DiscountsPage');
const GeneratedSettingsPage = lazyRouteComponent(generated, 'GeneratedSettingsPage');
const CustomerTipsPage = lazyRouteComponent(() => import('../features/settings/CustomerTipsPage.tsx'), 'CustomerTipsPage');
import { FeatureGate } from '../features/billing/FeatureGate.tsx';
import { LocationLayout } from './LocationLayout.tsx';
import { LoaderLayout } from '../shell/LoaderLayout.tsx';
import { Navigate } from '@tanstack/react-router';
import { pathFor } from '../router-paths.ts';
import { locationsOf } from '../session/session.ts';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    readonly title?: string;
  }
}

const root = createRootRoute({ component: Outlet });

/**
 * Legacy's guest-only pages (router/index.ts `requiresGuest`): someone already signed in goes home instead, where `/`
 * sends Radish staff and resellers to Accounts and a merchant to their dashboard. A reload holds no session in memory,
 * so the refresh cookie is tried once first; with none it fails quietly and the form shows.
 */
const guestOnly = async () => {
  if (sessionSnapshot() === null) await refreshSession();
  if (sessionSnapshot() !== null) throw redirect({ to: '/' });
};

const login = createRoute({
  getParentRoute: () => root,
  path: '/login',
  beforeLoad: guestOnly,
  validateSearch: z.object({ time: z.union([z.number(), z.string()]).optional(), expired: z.boolean().optional(), reset: z.boolean().optional() }),
  component: function Login() {
    const search = login.useSearch();
    return <LoginPage expired={search.expired === true} reset={search.reset === true} />;
  },
});


const forgot = createRoute({ getParentRoute: () => root, path: '/forgot-password', beforeLoad: guestOnly, component: ForgotPasswordPage });
/** A reset link signs out whoever is signed in first, as legacy's guard does (`logoutUser()`); an invitation does not. */
const reset = createRoute({
  getParentRoute: () => root, path: '/password/reset/$token',
  beforeLoad: () => endSession(),
  component: function Reset() {
    return <ResetPasswordPage token={reset.useParams().token} />;
  },
});
/** The merchant's menu review link (lane O): signed in by the link itself, never by a back-office session. */
const review = createRoute({ getParentRoute: () => root, path: '/review', component: lazyRouteComponent(() => import('../features/onboarding/ReviewPage.tsx'), 'ReviewPage') });
/** The client's Launchpad share link (ops plan 2026-10-17-launchpad-share): signed in by the link itself, view only. */
const watch = createRoute({ getParentRoute: () => root, path: '/watch', component: lazyRouteComponent(() => import('../features/onboarding/WatchPage.tsx'), 'WatchPage') });
const invite = createRoute({
  getParentRoute: () => root, path: '/invite/accept/$token',
  component: function Invite() {
    return <AcceptInvitePage token={invite.useParams().token} />;
  },
});

/** The login page, saying the session expired when it did (REG-006). */
const toLogin = () => ({ to: '/login' as const, search: loginSearch() });

/**
 * A page that needs a session: a reload has none in memory, so the refresh cookie is tried once first. Signed in
 * without an account chosen (Radish staff, resellers) goes to the account picker.
 */
const requireSession = async () => {
  if (currentSession() === null && sessionSnapshot() === null) await refreshSession();
  if (currentSession() !== null) return;
  if (choosingAccount() !== null) throw redirect({ to: '/accounts' });
  throw redirect(toLogin());
};

const accounts = createRoute({
  getParentRoute: () => root, path: '/accounts',
  beforeLoad: async () => {
    if (sessionSnapshot() === null) await refreshSession();
    const signed = sessionSnapshot();
    if (signed === null) throw redirect(toLogin());
    // B1728: legacy's Accounts list is REQUIRES_ADMIN_OR_RESELLER, and its guard sends a merchant to their dashboard with
    // no toast (`meta.users`, WF-001 step 7). Next's members of more than one account keep it: it is where they pick one.
    const view = signed.session;
    // Only one with a location to go to: Home sends a session with none back here (#2708 review N1).
    if (view.current !== null && staffMenuOf(view) === null && view.accounts.length <= 1 && locationsOf(view).length > 0) throw redirect({ to: '/' });
  },
  component: AccountsPage,
});

/**
 * The staff account pages (legacy REQUIRES_ADMIN_OR_RESELLER): only an account the person opens as Radish staff or its
 * reseller; anyone else goes home. The session acts in the account (no location) before the page reads it.
 */
const staffAccount = async ({ params }: { readonly params: { readonly accountId: string } }) => {
  if (sessionSnapshot() === null) await refreshSession();
  const signed = sessionSnapshot();
  if (signed === null) throw redirect(toLogin());
  if (!signed.session.accounts.some((a) => a.accountId === params.accountId && a.via !== 'membership')) throw redirect({ to: '/' });
  if (signed.session.current?.accountId !== params.accountId && !(await switchTo(params.accountId, null)).ok) throw redirect({ to: '/accounts' });
};

const accountDetail = createRoute({
  getParentRoute: () => root, path: '/accounts/$accountId', beforeLoad: staffAccount,
  component: function AccountDetail() {
    return <AccountDetailPage accountId={accountDetail.useParams().accountId} />;
  },
});

const staffLocation = createRoute({
  getParentRoute: () => root, path: '/accounts/$accountId/location/$locationId', beforeLoad: staffAccount,
  component: function StaffLocation() {
    const { accountId, locationId } = staffLocation.useParams();
    return <StaffLocationPage accountId={accountId} locationId={locationId} />;
  },
});

/** SCR-023 Activate a Device (legacy `/device-claims`, REQUIRES_ADMIN_OR_RESELLER): Radish staff and resellers only. */
const deviceClaimsRoute = createRoute({
  getParentRoute: () => root, path: '/device-claims',
  beforeLoad: async () => {
    if (sessionSnapshot() === null) await refreshSession();
    const signed = sessionSnapshot();
    if (signed === null) throw redirect(toLogin());
    if (staffMenuOf(signed.session) === null) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(deviceClaims, 'DeviceClaimsPage'),
});

/**
 * SCR-024 the staff QR Templates page (legacy `/qr-templates`, REQUIRES_ADMIN_OR_RESELLER): the premade set, written by
 * Radish staff and resellers only (the server refuses anyone else: `qr.premade.manage`, B875).
 */
const staffQrTemplatesRoute = createRoute({
  getParentRoute: () => root, path: '/qr-templates',
  beforeLoad: async () => {
    if (sessionSnapshot() === null) await refreshSession();
    const signed = sessionSnapshot();
    if (signed === null) throw redirect(toLogin());
    if (staffMenuOf(signed.session) === null) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(staffQrTemplates, 'StaffQrTemplatesPage'),
});

/**
 * SCR-011/012 the staff Users page (legacy `/users` → `/users/list`, REQUIRES_ADMIN): Radish admins only. Support and
 * viewer staff, resellers and everyone else go home (the server refuses them too: `users.platform.view`).
 */
const staffAdminOnly = async () => {
  if (sessionSnapshot() === null) await refreshSession();
  const signed = sessionSnapshot();
  if (signed === null) throw redirect(toLogin());
  if (signed.session.user.staffRole !== 'admin') throw redirect({ to: '/' });
};

const staffUsersRedirect = createRoute({
  getParentRoute: () => root, path: '/users', beforeLoad: async () => {
    await staffAdminOnly();
    throw redirect({ to: '/users/list' });
  },
});

const staffUsersRoute = createRoute({
  getParentRoute: () => root, path: '/users/list', beforeLoad: staffAdminOnly,
  component: lazyRouteComponent(staffUsers, 'StaffUsersPage'),
});

/**
 * `/` for Radish staff and resellers (SCR-168/169): legacy has no dashboard for them. Its `/` goes to Log in, whose
 * guest-only guard sends a signed-in admin or reseller to Accounts even while logged into a location (router/index.ts
 * `requiresGuest`; the admin and reseller DashboardView push to Accounts too). A redirect, so no empty page flashes.
 */
const homeGuard = async () => {
  await requireSession();
  const signed = sessionSnapshot();
  if (signed === null || staffMenuOf(signed.session) === null) return;
  // Legacy also clears the location they were logged into (setDefaultLocation(null)); the account stays open.
  const current = signed.session.current;
  if (current !== null && current.locationId !== null) await switchTo(current.accountId, null);
  throw redirect({ to: '/accounts' });
};

/**
 * `/l/$slug` for Radish staff and resellers: legacy logs them into the location a link names, in whichever account it
 * belongs to (router/index.ts, "auto-switch to logged in as user mode"). A slug none of their accounts has goes to
 * Accounts when no location is open; with one open, the location layout sends it there. Merchants are unchanged.
 */
const locationGuard = async ({ params }: { readonly params: { readonly slug: string } }) => {
  if (currentSession() === null && sessionSnapshot() === null) await refreshSession();
  const signed = sessionSnapshot();
  if (signed === null || staffMenuOf(signed.session) === null) return requireSession();
  const view = signed.session;
  const inAccount = view.accounts.find((a) => a.accountId === view.current?.accountId);
  const owner = [inAccount, ...view.accounts].find((a) => a?.locations.some((l) => l.slug === params.slug));
  const target = owner?.locations.find((l) => l.slug === params.slug);
  if (owner === undefined || target === undefined) {
    if (view.current === null || view.current.locationId === null) throw redirect({ to: '/accounts' });
    return;
  }
  if (view.current?.accountId === owner.accountId) return;
  if (!(await switchTo(owner.accountId, target.locationId)).ok) throw redirect({ to: '/accounts' });
};

/**
 * `/`: the dashboard of the location the session acts in, else the first (legacy routes.md §2 steps 5 and 7: the
 * default location's dashboard, the one last switched to; the server signs a merchant in there, EP-AUTH-05). WF-001.
 */
function Home() {
  const session = currentSession();
  const all = session === null ? [] : locationsOf(session.view);
  const here = all.find((l) => l.locationId === session?.view.current.locationId) ?? all[0];
  return here === undefined ? <Navigate to="/accounts" /> : <Navigate to={pathFor('/l/$slug', here.slug)} />;
}

/** `/not-found` (legacy BASE_ROUTES.NOT_FOUND): open to everyone, signed in or not. */
const notFound = createRoute({ getParentRoute: () => root, path: '/not-found', component: NotFoundPage });

/**
 * Every other URL (legacy's last route, `/:catchAll(.*)*`) goes to Not Found, before any guard: legacy's 14 shadowed
 * settings pages (SCR-167, routes.md §7.13) included, signed out too.
 */
const catchAll = createRoute({ getParentRoute: () => root, path: '$', beforeLoad: () => { throw redirect({ to: '/not-found', replace: true }); } });

const home = createRoute({ getParentRoute: () => root, path: '/', beforeLoad: homeGuard, component: Home });

const location = createRoute({ getParentRoute: () => root, path: '/l/$slug', beforeLoad: locationGuard, component: LocationLayout });

/** `/admin-center/$accountId` (legacy admin-center routes): its own shell; Gift Cards, and Billing for Radish staff. */
const adminCenterRoot = createRoute({ getParentRoute: () => root, path: '/admin-center/$accountId', beforeLoad: requireSession, component: lazyRouteComponent(adminCenterLayout, 'AdminCenterLayout') });

const page = (path: string, title: string, component: RouteComponent) =>
  createRoute({ getParentRoute: () => location, path, staticData: { title }, component });

/** A page of a billing feature (slice 6): the page where the location is entitled to it, else its upsell. */
const gated = (featureKey: string, Component: RouteComponent): RouteComponent => function Gated() {
  return <FeatureGate featureKey={featureKey}><Component /></FeatureGate>;
};

const to = (path: string) => ({ params }: { params: { slug: string } }) => {
  throw redirect({ to: `/l/${params.slug}/${path}` });
};

const routeTree = root.addChildren([
  login,
  forgot,
  reset,
  invite,
  review,
  watch,
  accounts,
  accountDetail,
  staffLocation,
  deviceClaimsRoute,
  staffQrTemplatesRoute,
  staffUsersRedirect,
  staffUsersRoute,
  home,
  notFound,
  catchAll,
  location.addChildren([
    page('/', 'Dashboard', lazyRouteComponent(dashboard, 'DashboardPage')),
    // Legacy's access-denied route has no title: its toolbar is blank.
    page('access-denied', '', lazyRouteComponent(() => import('../features/access/AccessDeniedPage.tsx'), 'AccessDeniedPage')),
    createRoute({ getParentRoute: () => location, path: 'discounts', beforeLoad: to('discounts/preset') }),
    page('discounts/preset', 'Discounts', () => <DiscountsPage kind="preset" />),
    page('discounts/auto', 'Discounts', () => <DiscountsPage kind="auto" />),
    page('discounts/price-adjustments', 'Discounts', () => <DiscountsPage kind="price" />),
    createRoute({ getParentRoute: () => location, path: 'menu', beforeLoad: to('menu/items') }),
    page('menu/items/{-$itemId}', 'Menu', lazyRouteComponent(items, 'ItemsPage')),
    page('menu/categories', 'Menu', lazyRouteComponent(menu, 'CategoriesPage')),
    page('menu/modifiers', 'Menu', lazyRouteComponent(modifiers, 'ModifiersPage')),
    page('menu/departments', 'Menu', lazyRouteComponent(departments, 'DepartmentsPage')),
    page('menu/display-reorder', 'Menu', lazyRouteComponent(displayOrder, 'DisplayOrderPage')),
    page('menu/visibility', 'Menu', lazyRouteComponent(visibility, 'CategoryVisibilityPage')),
    createRoute({ getParentRoute: () => location, path: 'employees', beforeLoad: to('employees/list') }),
    page('employees/list', 'Employees', lazyRouteComponent(employees, 'EmployeesPage')),
    page('employees/timecards/{-$timecardId}', 'Employees', gated('timecards_payroll', lazyRouteComponent(timecards, 'TimecardsPage'))),
    page('employees/schedule', 'Employees', lazyRouteComponent(schedule, 'SchedulePage')),
    page('employees/schedule/templates', 'Employees', lazyRouteComponent(scheduleTemplates, 'ScheduleTemplatesPage')),
    page('employees/availability', 'Employees', lazyRouteComponent(availability, 'AvailabilityPage')),
    page('employees/time-off', 'Employees', lazyRouteComponent(timeOff, 'TimeOffPage')),
    page('employees/shift-swaps', 'Employees', lazyRouteComponent(shiftSwaps, 'ShiftSwapsPage')),
    page('employees/shift-claims', 'Employees', lazyRouteComponent(shiftClaims, 'ShiftClaimsPage')),
    // Legacy's employees/roles renders a bare "Job Roles" stub; the job roles live in Settings › Employee Roles (SCR-075, Q-EMP-05 FIX).
    createRoute({ getParentRoute: () => location, path: 'employees/roles', beforeLoad: to('settings/staff-settings/employee-roles') }),
    page('employees/$employeeId', 'Employees', lazyRouteComponent(employeeDetail, 'EmployeeDetailPage')),
    page('settings/staff-settings/employee-roles', 'Employee Roles', lazyRouteComponent(jobRoles, 'JobRolesPage')),
    // Legacy redirects `customers` to its one tab, Customer Directory (CUS-001).
    createRoute({ getParentRoute: () => location, path: 'customers', beforeLoad: to('customers/list') }),
    page('customers/list', 'Customers', lazyRouteComponent(customers, 'CustomersPage')),
    page('customers/$customerId', 'Customers', lazyRouteComponent(customerDetail, 'CustomerDetailPage')),
    page('floor-plans', 'Floor Plans', lazyRouteComponent(floor, 'FloorPlansPage')),
    page('order/lookup', 'Order Lookup', lazyRouteComponent(orderLookup, 'OrderLookupPage')),
    // QR Table Pay: no sidebar entry, reached by its address (G-B10). Legacy redirects it to its first tab, QR Codes.
    createRoute({ getParentRoute: () => location, path: 'qr-table-pay', beforeLoad: to('qr-table-pay/qr-codes') }),
    page('qr-table-pay/qr-codes', 'QR Table Pay', lazyRouteComponent(qrCodes, 'QrCodesPage')),
    page('qr-table-pay/templates', 'QR Table Pay', lazyRouteComponent(qrTemplates, 'TemplatesPage')),
    page('qr-table-pay/settings', 'QR Table Pay', lazyRouteComponent(qrSettings, 'QrSettingsPage')),
    // Legacy redirects `online-store` to its first tab, Store Info.
    createRoute({ getParentRoute: () => location, path: 'online-store', beforeLoad: to('online-store/info') }),
    page('online-store/info', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineForms, 'StoreInfoPage'))),
    page('online-store/orders', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineForms, 'OnlineOrdersPage'))),
    page('online-store/hours-and-closures', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineHours, 'HoursAndClosuresPage'))),
    page('online-store/prep-times', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineLists, 'PrepTimesPage'))),
    page('online-store/banners', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineLists, 'BannersPage'))),
    page('online-store/notifications', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineForms, 'NotificationsPage'))),
    page('online-store/voice-ordering', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineVoice, 'VoiceOrderingPage'))),
    page('online-store/voice-conversations', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineVoice, 'VoiceConversationsPage'))),
    page('online-store/voice-conversations/$conversationId', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineVoice, 'VoiceConversationPage'))),
    page('online-store/api-keys', 'Online Store', gated('online_ordering', lazyRouteComponent(onlineKeys, 'ApiKeysPage'))),
    // Legacy reports-routes.ts with its hub flag (betaHubNavigation) off, its default: `reports` and each section's hub
    // URL open the section's first report (SCR-034, SCR-035, SCR-036, SCR-037); the hub pages themselves are not built.
    createRoute({ getParentRoute: () => location, path: 'reports', beforeLoad: to('reports/sales/overview') }),
    createRoute({ getParentRoute: () => location, path: 'reports/sales', beforeLoad: to('reports/sales/overview') }),
    createRoute({ getParentRoute: () => location, path: 'reports/audit', beforeLoad: to('reports/audit/drawer') }),
    createRoute({ getParentRoute: () => location, path: 'reports/management', beforeLoad: to('reports/management/payroll') }),
    page('reports/sales/overview', 'Reports', lazyRouteComponent(reports, 'OverviewPage')),
    // Legacy's Payment Methods drill-in never rendered and nothing linked to it (B116, Q-RPT-09): retired, its URL opens the Overview.
    createRoute({ getParentRoute: () => location, path: 'reports/sales/payments', beforeLoad: to('reports/sales/overview') }),
    page('reports/sales/consolidated', 'Reports', lazyRouteComponent(() => import('../features/reports/EodPage.tsx'), 'EodPage')),
    page('reports/sales/items', 'Reports', lazyRouteComponent(salesReports, 'ItemSalesPage')),
    page('reports/sales/category', 'Reports', lazyRouteComponent(salesReports, 'CategorySalesPage')),
    page('reports/sales/department', 'Reports', lazyRouteComponent(salesReports, 'DepartmentSalesPage')),
    page('reports/sales/dining', 'Reports', lazyRouteComponent(salesReports, 'DiningOptionSalesPage')),
    page('reports/sales/time-segment', 'Reports', lazyRouteComponent(salesReports, 'TimeSegmentSalesPage')),
    page('reports/sales/employee', 'Reports', lazyRouteComponent(reports, 'EmployeeSalesPage')),
    page('reports/sales/discounts', 'Reports', lazyRouteComponent(() => import('../features/reports/DiscountsPage.tsx'), 'DiscountsPage')),
    page('reports/sales/gift-cards', 'Reports', lazyRouteComponent(giftCards, 'GiftCardsReportPage')),
    page('reports/sales/gift-cards/welcome', 'Reports', lazyRouteComponent(giftCardsWelcome, 'GiftCardsWelcomePage')),
    page('reports/audit/drawer', 'Reports', lazyRouteComponent(drawersList, 'DrawersPage')),
    page('reports/audit/server-banks', 'Reports', lazyRouteComponent(drawersList, 'ServerBanksPage')),
    // Legacy's reports/audit/drawer/:id?/orders mounts its drawer page's orders table alone, always empty; a drawer's
    // orders are its page's Orders tab, the tab it opens on (SCR-053, B565 FIX). No id names no drawer: the list.
    createRoute({ getParentRoute: () => location, path: 'reports/audit/drawer/orders', beforeLoad: to('reports/audit/drawer') }),
    createRoute({
      getParentRoute: () => location, path: 'reports/audit/drawer/$tillSessionId/orders',
      beforeLoad: ({ params }: { params: { slug: string; tillSessionId: string } }) => { throw redirect({ to: `/l/${params.slug}/reports/audit/drawer/${params.tillSessionId}` }); },
    }),
    page('reports/audit/drawer/$tillSessionId', 'Reports', lazyRouteComponent(() => import('../features/reports/DrawerDetailPage.tsx'), 'DrawerDetailPage')),
    // Legacy opens Adjustments on its Voids tab.
    createRoute({ getParentRoute: () => location, path: 'reports/audit/adjustments', beforeLoad: to('reports/audit/adjustments/voids') }),
    page('reports/audit/adjustments/voids', 'Reports', lazyRouteComponent(adjustments, 'AdjustmentVoidsPage')),
    page('reports/audit/refunds', 'Reports', lazyRouteComponent(() => import('../features/reports/RefundsPage.tsx'), 'RefundsPage')),
    page('reports/audit/adjustments/deletion', 'Reports', lazyRouteComponent(adjustments, 'AdjustmentDeletionsPage')),
    page('reports/management/payroll', 'Reports', lazyRouteComponent(payroll, 'PayrollPage')),
    page('reports/management/breaks', 'Reports', lazyRouteComponent(breaksReport, 'BreaksPage')),
    // Legacy's Settings root and section roots with hub navigation off (Split betaHubNavigation, its default; the hub
    // pages wait on GATE-19): each lands on its first leaf (SCR-120, routes.md §7.12).
    createRoute({ getParentRoute: () => location, path: 'settings', beforeLoad: to('settings/location/business-profile') }),
    createRoute({ getParentRoute: () => location, path: 'settings/location', beforeLoad: to('settings/location/business-profile') }),
    createRoute({ getParentRoute: () => location, path: 'settings/staff-settings', beforeLoad: to('settings/staff-settings/timecards/employee-breaks') }),
    createRoute({ getParentRoute: () => location, path: 'settings/order-checkout', beforeLoad: to('settings/order-checkout/pos-functions/register-settings') }),
    createRoute({ getParentRoute: () => location, path: 'settings/order-checkout/pos-functions', beforeLoad: to('settings/order-checkout/pos-functions/register-settings') }),
    createRoute({ getParentRoute: () => location, path: 'settings/order-checkout/tips', beforeLoad: to('settings/order-checkout/tips/customer-tips') }),
    // Legacy redirects `settings/hardware` to its first tab, POS Devices.
    createRoute({ getParentRoute: () => location, path: 'settings/hardware', beforeLoad: to('settings/hardware/devices') }),
    page('settings/hardware/devices', 'Hardware', lazyRouteComponent(devices, 'PosDevicesPage')),
    // Business Profile opens on Location Info.
    page('settings/location/business-profile', 'Business Profile', lazyRouteComponent(locationInfo, 'LocationInfoPage')),
    page('settings/location/business-profile/business-hours', 'Business Profile', lazyRouteComponent(() => import('../features/branding/BusinessHoursPage.tsx'), 'BusinessHoursPage')),
    page('settings/location/business-profile/branding', 'Business Profile', lazyRouteComponent(branding, 'BrandingPage')),
    page('settings/location/payment-configuration', 'Payment Configuration', lazyRouteComponent(payment, 'PaymentConfigurationPage')),
    page('settings/location/plans-and-billing', 'Plans & Billing', lazyRouteComponent(plansAndBilling, 'PlansAndBillingPage')),
    page('profile', 'Profile', lazyRouteComponent(profile, 'ProfilePage')),
    page('settings/location/users', 'Users', lazyRouteComponent(users, 'UsersPage')),
    page('settings/staff-settings/user-roles', 'User Roles', lazyRouteComponent(roles, 'UserRolesPage')),
    page('settings/staff-settings/scheduling', 'Scheduling', lazyRouteComponent(scheduling, 'SchedulingSettingsPage')),
    page('settings/location/business-day', 'Business Day', () => <GeneratedSettingsPage layoutId="location.business-day" />),
    createRoute({ getParentRoute: () => location, path: 'settings/staff-settings/timecards', beforeLoad: to('settings/staff-settings/timecards/employee-breaks') }),
    page('settings/staff-settings/timecards/employee-breaks', 'Timecards', gated('timecards_payroll', lazyRouteComponent(breaks, 'EmployeeBreaksPage'))),
    page('settings/staff-settings/timecards/report-settings', 'Timecards', () => <GeneratedSettingsPage layoutId="staff-settings.timecards.report-settings" />),
    page('settings/hardware/printers', 'Hardware', lazyRouteComponent(hardware, 'PrintersPage')),
    page('settings/staff-settings/timecards/overtime', 'Timecards', lazyRouteComponent(overtime, 'OvertimeSettingsPage')),
    page('settings/order-checkout/pos-functions/register-settings', 'POS Functions', () => <GeneratedSettingsPage layoutId="order-checkout.register-settings" />),
    page('settings/order-checkout/pos-functions/drawers', 'POS Functions', () => <GeneratedSettingsPage layoutId="order-checkout.drawers" />),
    page('settings/order-checkout/pos-functions/voids', 'POS Functions', lazyRouteComponent(voids, 'VoidReasonsPage')),
    page('settings/order-checkout/pos-functions/dining-options', 'POS Functions', lazyRouteComponent(diningOptions, 'DiningOptionsPage')),
    page('settings/order-checkout/pos-functions/tender-types', 'POS Functions', lazyRouteComponent(tenderTypes, 'TenderTypesPage')),
    page('settings/order-checkout/pos-functions/pacing', 'POS Functions', lazyRouteComponent(pacing, 'PacingPage')),
    page('settings/order-checkout/pos-functions/id-verification', 'POS Functions', lazyRouteComponent(idVerification, 'IdVerificationPage')),
    page('settings/order-checkout/print-options', 'Print Options', () => <GeneratedSettingsPage layoutId="order-checkout.print-options" />),
    page('settings/order-checkout/tips/customer-tips', 'Tips', () => <CustomerTipsPage />),
    page('settings/order-checkout/tips/staff-tips', 'Tips', lazyRouteComponent(staffTips, 'StaffTipsPage')),
    // Legacy redirects `settings/reporting` to its first tab, Time Segments.
    createRoute({ getParentRoute: () => location, path: 'settings/reporting', beforeLoad: to('settings/reporting/time-segments') }),
    page('settings/reporting/time-segments', 'Reporting', lazyRouteComponent(timeSegments, 'TimeSegmentsPage')),
    page('settings/reporting/departments', 'Reporting', lazyRouteComponent(departments, 'ReportingDepartmentsPage')),
  ]),
  adminCenterRoot.addChildren([
    createRoute({ getParentRoute: () => adminCenterRoot, path: 'gift-cards', staticData: { title: 'Admin Center' }, component: lazyRouteComponent(adminCenter, 'AdminCenterGiftCardsPage') }),
    createRoute({ getParentRoute: () => adminCenterRoot, path: 'billing/features', staticData: { title: 'Admin Center' }, component: lazyRouteComponent(billingFeatures, 'BillingFeaturesPage') }),
    createRoute({ getParentRoute: () => adminCenterRoot, path: 'billing/plans', staticData: { title: 'Admin Center' }, component: lazyRouteComponent(billingPlans, 'BillingPlansPage') }),
    createRoute({ getParentRoute: () => adminCenterRoot, path: 'billing/adjustments', staticData: { title: 'Admin Center' }, component: lazyRouteComponent(billingAdjustments, 'BillingAdjustmentsPage') }),
    createRoute({ getParentRoute: () => adminCenterRoot, path: 'billing/not-yet-on-plan', staticData: { title: 'Admin Center' }, component: lazyRouteComponent(billingNotYetOnPlan, 'BillingNotYetOnPlanPage') }),
  ]),
]);

/** The router; tests pass a memory history. */
/**
 * Until the first navigation resolves (its guards may wait on the refresh cookie, and nothing of the page draws until they
 * finish), the loader layout (SCR-170), as legacy App.vue shows LoaderLayout before a route names its layout. Later
 * navigations keep the page they leave until the next one is ready.
 */
function Booting({ children }: { readonly children: ReactNode }) {
  const booting = useRouterState({ select: (s) => s.resolvedLocation === undefined });
  return <>{booting ? <LoaderLayout /> : null}{children}</>;
}

export function createAppRouter(history?: RouterHistory) {
  return createRouter({ routeTree, defaultPreload: false, scrollRestoration: true, InnerWrap: Booting, ...(history === undefined ? {} : { history }) });
}

